/**
 * @file DateRangePicker.tsx
 * @brief Date-range filter field: a pill trigger that opens `DateRangePopover`.
 *
 * @details
 * Trigger: `[ Calendar | 3 Mar 2026 → 26 May 2026 | × ]`
 *
 * DES (30-09-2026): "poori desktop app main date range picker bhi aik hi kar
 * do ... use the new one you made". The side sheet with its own calendars is
 * gone: the field opens the same `DateRangePopover` as the Boardrooms date
 * filter (presets on the left, two months, Clear / Filter) under itself.
 * Props are unchanged, so every filter bar moved with no edits; it still
 * emits ISO day bounds (start of the first day, end of the last).
 *
 * - Presets: Today … Last year. "All time" is Clear (no bounds), as before.
 * - The × on the trigger clears without opening.
 *
 * @author DES
 * @date 26-05-2026
 * @updated 30-07-2026 — dual-month + presets (web range UI)
 * @updated 30-09-2026 — opens DateRangePopover; the sheet is removed
 * @updated 01-10-2026 — `variant="circle"`: the Notes/Boardrooms 56px
 *          calendar circle for a page's top right (red when a range is set,
 *          with a corner ✕), DES "session screen per bhi isay top per".
 */

import { useMemo, useState } from "react";
import {
  endOfDay,
  endOfMonth,
  endOfWeek,
  endOfYear,
  format,
  isSameDay,
  parseISO,
  startOfDay,
  startOfMonth,
  startOfWeek,
  startOfYear,
  subDays,
  subMonths,
  subWeeks,
  subYears,
} from "date-fns";
import { Calendar as CalendarIcon, CalendarDays, X } from "lucide-react";
import {
  DateRangePopover,
  type DateRangePresetDef,
} from "@/components/ui/DateRangePopover";
import { cn } from "@/shared/theme/cn";

function toDate(value: string | Date | null | undefined): Date | null {
  if (!value) return null;
  if (value instanceof Date) return Number.isNaN(value.getTime()) ? null : value;
  const d = parseISO(value);
  return Number.isNaN(d.getTime()) ? null : d;
}

/** Weeks start on Monday, like the calendars. */
const PRESETS: DateRangePresetDef[] = [
  {
    id: "today",
    label: "Today",
    range: () => ({ from: startOfDay(new Date()), to: endOfDay(new Date()) }),
  },
  {
    id: "yesterday",
    label: "Yesterday",
    range: () => {
      const y = subDays(startOfDay(new Date()), 1);
      return { from: y, to: endOfDay(y) };
    },
  },
  {
    id: "this-week",
    label: "This week",
    range: () => ({
      from: startOfWeek(new Date(), { weekStartsOn: 1 }),
      to: endOfWeek(new Date(), { weekStartsOn: 1 }),
    }),
  },
  {
    id: "last-week",
    label: "Last week",
    range: () => {
      const ref = subWeeks(new Date(), 1);
      return {
        from: startOfWeek(ref, { weekStartsOn: 1 }),
        to: endOfWeek(ref, { weekStartsOn: 1 }),
      };
    },
  },
  {
    id: "this-month",
    label: "This month",
    range: () => ({ from: startOfMonth(new Date()), to: endOfMonth(new Date()) }),
  },
  {
    id: "last-month",
    label: "Last month",
    range: () => {
      const ref = subMonths(new Date(), 1);
      return { from: startOfMonth(ref), to: endOfMonth(ref) };
    },
  },
  {
    id: "this-year",
    label: "This year",
    range: () => ({ from: startOfYear(new Date()), to: endOfYear(new Date()) }),
  },
  {
    id: "last-year",
    label: "Last year",
    range: () => {
      const ref = subYears(new Date(), 1);
      return { from: startOfYear(ref), to: endOfYear(ref) };
    },
  },
];

/** The preset a saved range is, so its pill shows as chosen on reopen. */
function presetFor(from: Date | null, to: Date | null): string | null {
  if (!from || !to) return null;
  const match = PRESETS.find((p) => {
    const r = p.range();
    return isSameDay(r.from, from) && isSameDay(r.to, to);
  });
  return match?.id ?? null;
}

export interface DateRangePickerProps {
  from?: string | null;
  to?: string | null;
  onChange: (next: { from?: string; to?: string }) => void;
  placeholder?: string;
  size?: "sm" | "md";
  disabled?: boolean;
  className?: string;
  /** Controlled open state (optional). */
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  /** `field` (default): the pill. `circle`: the page-header calendar circle. */
  variant?: "field" | "circle";
}

export function DateRangePicker({
  from,
  to,
  onChange,
  placeholder = "Pick a date range",
  size = "md",
  disabled,
  className,
  open: openProp,
  onOpenChange,
  variant = "field",
}: DateRangePickerProps) {
  const [openState, setOpenState] = useState(false);
  const open = openProp ?? openState;
  const setOpen = (next: boolean) => {
    if (openProp === undefined) setOpenState(next);
    onOpenChange?.(next);
  };

  const fromDate = toDate(from);
  const toDateValue = toDate(to);
  const triggerLabel = useMemo(() => {
    if (fromDate && toDateValue) {
      return `${format(fromDate, "d MMM yyyy")} → ${format(toDateValue, "d MMM yyyy")}`;
    }
    if (fromDate) return `From ${format(fromDate, "d MMM yyyy")}`;
    if (toDateValue) return `Until ${format(toDateValue, "d MMM yyyy")}`;
    return null;
  }, [fromDate, toDateValue]);

  const clear = () => onChange({ from: undefined, to: undefined });
  const triggerHeight = size === "sm" ? "h-9" : "h-10";

  const popoverProps = {
    open,
    onOpenChange: setOpen,
    presets: PRESETS,
    value: {
      presetId: presetFor(fromDate, toDateValue),
      from: fromDate,
      to: toDateValue,
    },
    onApply: (v: { from: Date; to: Date }) =>
      onChange({
        from: startOfDay(v.from).toISOString(),
        to: endOfDay(v.to).toISOString(),
      }),
    onClear: clear,
  };

  if (variant === "circle") {
    const active = Boolean(triggerLabel);
    return (
      <span className={cn("relative inline-flex shrink-0", className)}>
        <DateRangePopover
          {...popoverProps}
          anchor={
            <button
              type="button"
              disabled={disabled}
              aria-haspopup="dialog"
              aria-expanded={open}
              onClick={() => !disabled && setOpen(!open)}
              aria-label={triggerLabel ? `Dates: ${triggerLabel}` : "Filter by date"}
              title={triggerLabel ?? "Filter by date"}
              className={cn(
                "flex h-14 w-14 items-center justify-center rounded-full border transition-colors disabled:opacity-50",
                active
                  ? "border-brand bg-brand text-white hover:bg-brand/90"
                  : "border-black/5 bg-black/[0.04] text-brand hover:bg-black/[0.08] dark:border-white/[0.07] dark:bg-white/[0.06]",
              )}
            >
              <CalendarDays className="h-6 w-6" strokeWidth={1.5} />
            </button>
          }
        />
        {active ? (
          <button
            type="button"
            onClick={clear}
            aria-label="Clear dates"
            className="absolute -right-1 -top-1 flex h-6 w-6 items-center justify-center rounded-full border-2 border-app-page bg-app-text text-app-page"
          >
            <X className="h-3.5 w-3.5" strokeWidth={2.5} />
          </button>
        ) : null}
      </span>
    );
  }

  return (
    <DateRangePopover
      open={open}
      onOpenChange={setOpen}
      presets={PRESETS}
      value={{
        presetId: presetFor(fromDate, toDateValue),
        from: fromDate,
        to: toDateValue,
      }}
      onApply={(v) =>
        onChange({
          from: startOfDay(v.from).toISOString(),
          to: endOfDay(v.to).toISOString(),
        })
      }
      onClear={clear}
      anchorClassName="flex w-full min-w-0"
      anchor={
        <button
          type="button"
          disabled={disabled}
          aria-haspopup="dialog"
          aria-expanded={open}
          onClick={() => !disabled && setOpen(!open)}
          className={cn(
            "group flex w-full items-center justify-between gap-2 rounded-full border border-input bg-field px-5 text-sm transition-colors",
            triggerHeight,
            "focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2",
            "disabled:cursor-not-allowed disabled:opacity-50",
            !triggerLabel && "text-muted-foreground",
            className,
          )}
        >
          <span className="flex min-w-0 items-center gap-2">
            <CalendarIcon className="h-4 w-4 shrink-0 opacity-60" />
            <span className="truncate">{triggerLabel ?? placeholder}</span>
          </span>
          {triggerLabel ? (
            <span
              role="button"
              aria-label="Clear date range"
              tabIndex={0}
              onClick={(e) => {
                e.stopPropagation();
                clear();
              }}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.stopPropagation();
                  e.preventDefault();
                  clear();
                }
              }}
              className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full text-muted-foreground transition-colors hover:bg-secondary hover:text-foreground"
            >
              <X className="h-3.5 w-3.5" />
            </span>
          ) : null}
        </button>
      }
    />
  );
}
